import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, ViewEncapsulation } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';

export type HxCardAppearance = 'outlined' | 'filled' | 'elevated';
export type HxCardHeight = 'fit-content' | string;

/**
 * Harmony Card Component
 *
 * A reusable card component that provides consistent visual styling for content organization following Material Design
 * principles. Uses design system tokens for all styling values.
 *
 * @example
 *   ```html
 *   <hx-card appearance="outlined" height="standard" title="Card Title" subtitle="Optional subtitle">
 *     <hx-card-content>
 *       <p>Card content goes here...</p>
 *     </hx-card-content>
 *     <hx-card-actions align="end">
 *       <button>Cancel</button>
 *       <button>Save</button>
 *     </hx-card-actions>
 *   </hx-card>
 *   ```;
 */
@Component({
  selector: 'hx-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './card.component.html',
  styleUrls: ['./card.css'],
  host: {
    class: 'hx-card',
    '[class.hx-card--outlined]': 'appearance() === "outlined"',
    '[class.hx-card--filled]': 'appearance() === "filled"',
    '[class.hx-card--elevated]': 'appearance() === "elevated"',
    '[class.hx-card--fit-content]': 'height() === "fit-content"',
    '[style.height]': 'height() !== "fit-content" ? height() : null',
    tabindex: '0',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxCard extends HxBaseControl {
  /**
   * The visual appearance of the card
   *
   * - Outlined: Card with border and no shadow
   * - Filled: Card with background fill
   * - Elevated: Card with shadow elevation (default)
   */
  readonly appearance = input<HxCardAppearance>('elevated');

  /**
   * The height of the card
   *
   * - 'fit-content': Height adjusts to content size (default)
   * - Any CSS height value with units: '100%', '300px', '50vh', etc.
   *
   * @example
   *   height = 'fit-content'; // Default, adjusts to content
   *   height = '100%'; // Full height of parent
   *   height = '300px'; // Fixed pixel height
   *   height = '50vh'; // 50% of viewport height
   */
  readonly height = input<HxCardHeight>('fit-content');

  /** Title text for the card header */
  readonly title = input<string>();

  /** Subtitle text for the card header */
  readonly subtitle = input<string>();

  /** Whether the title should be disabled (hidden) */
  readonly disabledTitle = input<boolean>(false);

  /** Whether the subtitle should be disabled (hidden) */
  readonly disabledSubtitle = input<boolean>(false);

  /** Check if the header should be shown */
  get showHeader(): boolean {
    return !!(this.title() && !this.disabledTitle()) || !!(this.subtitle() && !this.disabledSubtitle());
  }
}
